import memoize from 'lodash/memoize'

import { withCache } from '@app/utils/cache'
import { createSelector } from '@app/utils/createSelector'
import { catchFetchDataError } from '@app/utils/performFetchData'

import type { ApiFaq } from '@app/packages/api/v2/types/entities'
import type { FaqFilter } from '@app/packages/api/v2/types/params'
import { createLoadable } from '@app/packages/thunkSelector/thunkSelector'
import type { SliceState } from '@app/packages/thunkSelector/withCheckedSliceState'
import { withCheckedSliceState } from '@app/packages/thunkSelector/withCheckedSliceState'

import { callV2Api } from '@app/store/actions/api'
import { getServerCache } from '@app/store/actions/contexts/serverCache'
import { createReduxSlice } from '@app/store/redux_slice'
import { createThunk } from '@app/store/thunk'

import { routingDateSelector } from './routing'

const getFaqSlice = memoize((section: FaqSection) => createReduxSlice<SliceState<ApiFaq>>(`faq:${section}`))

export const createFaqSelector = memoize((section: FaqSection) => {
  const faqSlice = getFaqSlice(section)
  return createSelector([faqSlice.selector, routingDateSelector], (sliceState, date) =>
    createLoadable(c =>
      withCheckedSliceState(c, {
        sliceState,
        slice: faqSlice,
        date: { date, expiresInMinutes: 60 },
        load: async dispatch => {
          const cache = dispatch(getServerCache())
          return await withCache(cache, `9bde1c41-4323-4a6d-9c09-ffb9dd042d01 faq:${section}`, () => dispatch(fetchFaq(section)))
        },
      })
    )
  )
})

export type FaqSection = 'main' | 'parents/landing' | 'sitters/landing' | 'playground/landing' | 'sitters/knowledge'

export const fetchFaq = (section: FaqSection) =>
  createThunk(async dispatch => {
    const { getFaq } = await import('@app/packages/api/v2/actions/faq')
    const filter = ((): FaqFilter => {
      switch (section) {
        case 'main':
          return { 'filter[for_parents]': 'true' }
        case 'sitters/knowledge':
          return { 'filter[for_sitters]': 'true', 'filter[for_knowledgebase]': 'true' }
        case 'parents/landing':
          return { 'filter[for_parents]': 'true', 'filter[for_landing]': 'true' }
        case 'playground/landing':
          return { 'filter[for_playground]': 'true', 'filter[for_landing]': 'true' }
        case 'sitters/landing':
          return { 'filter[for_sitters]': 'true', 'filter[for_landing]': 'true' }
      }
    })()
    const resp = await dispatch(callV2Api(getFaq(filter))).catch(catchFetchDataError(dispatch, 'Faq fetch failed'))
    return resp.data
  })
